import { useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { clsx } from 'clsx';
import { Box } from '@unifyapps/ui/components/Box';
import { isNative } from '@unifyapps/ui/utils/isNative';
import { IconButton } from '@unifyapps/ui/components/IconButton';
import { getTextColor } from '@unifyapps/ui/components/Button/styles.native';
import SvgMicrophone01 from '@unifyapps/icons/outline/Microphone01';
import { iconButtonIconVariants } from '@unifyapps/ui/components/IconButton/styles';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { useSpeechToText } from '../../hooks/useSpeechRecognition/useSpeechToText';

export type DictationActionsRef = React.MutableRefObject<{
  startListening: () => void;
  stopListening: () => void;
} | null>;

type Props = {
  isLoading: boolean;
  disabled: boolean;
  input: string | undefined;
  onInputChange: (value: string) => void;
  onStopListening?: () => void;
  onStartDictation?: () => void;
  speechTimeout?: number;
  onSpeechTimeout?: () => void;
  color: 'brand' | 'neutral' | 'danger';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  variant: 'solid' | 'outline' | 'soft' | 'ghost';
  className?: string;
  language?: string;
  dictationActionsRef?: DictationActionsRef;
};

const ANIMATION_STYLE = {
  animationDuration: '2s',
};

const BUTTON_COLOR_TO_OUTLINE_COLOR = {
  brand: 'var(--palette-alpha-brand-30)',
  neutral: 'var(--palette-alpha-gray-30)',
  danger: 'var(--palette-alpha-error-30)',
};

const BUTTON_COLOR_TO_ANIMATION_BACKGROUND_CLASSNAME = {
  brand: 'bg-brand-solid',
  neutral: 'bg-solid-tertiary',
  danger: 'bg-error-solid',
};

export function SpeechToTextAction({
  onStopListening,
  onInputChange,
  speechTimeout,
  onSpeechTimeout,
  disabled,
  onStartDictation,
  input,
  color,
  size,
  variant,
  className,
  language,
  dictationActionsRef,
}: Props) {
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const stopListeningRef = useRef<(() => void) | undefined>(undefined);

  const onResult = useCallback(
    (text: string) => {
      /**
       * Only to be done when speechTimeout is non-zero
       * since we're considering 0 as a value to disable this feature
       */
      if (speechTimeout) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }

        timerRef.current = setTimeout(() => {
          if (text) {
            stopListeningRef.current?.();
            onSpeechTimeout?.();
          }
        }, speechTimeout);
      }

      onInputChange(text);
    },
    [onInputChange, onSpeechTimeout, speechTimeout],
  );

  const {
    startListening: _startListening,
    stopListening: _stopListening,
    isListening,
  } = useSpeechToText({
    onResult,
    language,
  });

  const startListening = useEventCallback(() => {
    onStartDictation?.();
    _startListening();
  });

  const stopListening = useEventCallback(() => {
    _stopListening();
    if (input) {
      onStopListening?.();
    }
  });
  stopListeningRef.current = stopListening;

  useImperativeHandle(
    dictationActionsRef,
    () => ({
      startListening,
      stopListening,
    }),
    [startListening, stopListening],
  );

  const onMicrophoneClick = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  const iconNode = useMemo(
    () => (
      <SvgMicrophone01
        className={clsx(
          iconButtonIconVariants({
            size,
          }),
          getTextColor({ variant, color }),
        )}
      />
    ),
    [color, size, variant],
  );

  const buttonStyle = useMemo(
    () => ({
      // All buttons except solid buttons doesn't have background, so our animation comes over the button
      // So, we need to set background color for all variants except solid
      backgroundColor:
        isListening && variant !== 'solid' ? 'var(--bg-primary) !important' : undefined,
      outline: isListening ? `3px solid ${BUTTON_COLOR_TO_OUTLINE_COLOR[color]}` : 'none',
    }),
    [color, isListening, variant],
  );

  return (
    <Box className={clsx('relative', className)}>
      {isListening ? (
        <Box
          className={clsx(
            {
              'absolute -start-[5px] -top-[5px] inline-flex h-[125%] w-[125%] animate-ping rounded-sm opacity-80':
                isNative(),
              'absolute start-[12%] top-[12%] inline-flex h-[75%] w-[75%] animate-ping rounded-sm opacity-80':
                !isNative(),
            },
            BUTTON_COLOR_TO_ANIMATION_BACKGROUND_CLASSNAME[color],
          )}
          style={ANIMATION_STYLE}
        />
      ) : null}
      <IconButton
        IconNode={iconNode}
        color={color}
        disabled={disabled}
        onClick={onMicrophoneClick}
        size={size}
        style={buttonStyle}
        variant={variant}
      />
    </Box>
  );
}
